import { ArrowDown, ArrowRight, ArrowUp } from 'lucide-react'
import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'

/**
 * Ported from pollsphere-bundle/src/components/erp/primitives.tsx.
 * Markup and classes are PollSphere's. Do not restyle.
 */

export function MetricCard({
  label, value, unit, delta, deltaType = 'neutral', sub, accent,
}: {
  label: string
  value: ReactNode
  unit?: string
  delta?: string
  deltaType?: 'up' | 'down' | 'neutral'
  /**
   * A node, not just a string: the line under a headline number is where a
   * period-on-period delta goes, and that carries an arrow and a colour with it.
   * `truncate` is dropped when it is a node — a delta that reads "up 38% vs previou…"
   * is worse than one that wraps.
   */
  sub?: ReactNode
  accent?: boolean
}) {
  return (
    <div className={cn(
      'erp-card px-4 py-3 flex flex-col gap-1 min-w-0',
      accent && 'border-l-[3px] border-l-chocolate bg-chocolate-soft/40',
    )}>
      <div className="text-erp-xs uppercase tracking-wide text-muted-foreground truncate">{label}</div>
      <div className="flex items-baseline gap-1.5">
        <span className="text-erp-xl font-semibold erp-metric tracking-tight">{value}</span>
        {unit && <span className="text-erp-xs text-muted-foreground">{unit}</span>}
        {delta && (
          <span className={cn(
            'ml-auto inline-flex items-center gap-0.5 text-erp-xs font-medium',
            deltaType === 'up' && 'text-emerald-700 dark:text-emerald-400',
            deltaType === 'down' && 'text-red-700 dark:text-red-400',
            deltaType === 'neutral' && 'text-muted-foreground',
          )}>
            {deltaType === 'up' && <ArrowUp className="h-3 w-3" />}
            {deltaType === 'down' && <ArrowDown className="h-3 w-3" />}
            {deltaType === 'neutral' && <ArrowRight className="h-3 w-3" />}
            {delta}
          </span>
        )}
      </div>
      {sub && (
        <div className={cn('text-erp-xs text-muted-foreground mt-0.5',
          typeof sub === 'string' && 'truncate')}>
          {sub}
        </div>
      )}
    </div>
  )
}

export function SectionHeader({
  title, description, right, dense,
}: { title: string; description?: string; right?: ReactNode; dense?: boolean }) {
  return (
    <div className={cn('flex items-end justify-between gap-3 axis-stack-sm', dense ? 'py-1.5' : 'pb-3')}>
      <div className="min-w-0">
        <h3 className="text-erp-lg font-semibold tracking-tight truncate">{title}</h3>
        {description && <p className="text-erp-xs text-muted-foreground truncate mt-0.5">{description}</p>}
      </div>
      {right && <div className="flex items-center gap-2 shrink-0">{right}</div>}
    </div>
  )
}

const statusColors: Record<string, string> = {
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  verified: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  delivered: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  published: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  sent: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  planning: 'bg-amber-50 text-amber-700 border-amber-200',
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  invited: 'bg-amber-50 text-amber-700 border-amber-200',
  scheduled: 'bg-amber-50 text-amber-700 border-amber-200',
  draft: 'bg-slate-100 text-slate-600 border-slate-200',
  suspended: 'bg-amber-50 text-amber-700 border-amber-200',
  deactivated: 'bg-slate-100 text-slate-600 border-slate-200',
  paused: 'bg-slate-100 text-slate-600 border-slate-200',
  weak: 'bg-red-50 text-red-700 border-red-200',
  failed: 'bg-red-50 text-red-700 border-red-200',
  rejected: 'bg-red-50 text-red-700 border-red-200',
  closed: 'bg-slate-100 text-slate-600 border-slate-200',
}

export function StatusBadge({ status, className }: { status: string; className?: string }) {
  const key = (status ?? '').toLowerCase()
  const cls = statusColors[key] ?? 'bg-muted text-muted-foreground border-border'
  const dot =
    ['active', 'verified', 'delivered', 'sent', 'published'].includes(key) ? 'bg-emerald-500'
    : ['planning', 'pending', 'scheduled', 'invited', 'suspended'].includes(key) ? 'bg-amber-500'
    : ['failed', 'rejected', 'weak'].includes(key) ? 'bg-red-500'
    : 'bg-slate-400'
  return (
    <span className={cn(
      'inline-flex items-center gap-1.5 px-2 py-0.5 text-erp-xs font-medium border rounded-md capitalize',
      cls, className,
    )}>
      <span className={cn('erp-status-dot', dot)} />
      {key.replace(/_/g, ' ')}
    </span>
  )
}

export function Panel({
  title, description, right, children, className, bodyClassName, dense,
}: {
  title?: string; description?: string; right?: ReactNode
  children: ReactNode; className?: string; bodyClassName?: string; dense?: boolean
}) {
  return (
    <div className={cn('erp-card flex flex-col min-w-0', className)}>
      {(title || right) && (
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border bg-muted/30 axis-stack-sm">
          <div className="min-w-0">
            {title && <h4 className="text-erp-md font-semibold tracking-tight truncate">{title}</h4>}
            {description && <p className="text-erp-xs text-muted-foreground truncate mt-0.5">{description}</p>}
          </div>
          {right && <div className="flex items-center gap-1.5 shrink-0">{right}</div>}
        </div>
      )}
      <div className={cn(dense ? 'p-3' : 'p-4', bodyClassName)}>{children}</div>
    </div>
  )
}

export function KpiStrip({ children }: { children: ReactNode }) {
  // `axis-kpi` is inert above 768px — the grid below is PollSphere's, unchanged.
  // Under 768px it becomes a snap carousel so five cards do not consume the
  // whole first screen before any data is visible.
  return (
    <div className="axis-kpi grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))' }}>
      {children}
    </div>
  )
}

/** SAP-style data grid. Horizontal scroll lives in the container, never the page. */
export function ErpTable({ head, children }: { head: ReactNode; children: ReactNode }) {
  return (
    <div className="axis-table-scroll">
      <table className="w-full border-collapse">
        <thead><tr>{head}</tr></thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  )
}

export function Th({
  children, className, title,
}: { children?: ReactNode; className?: string; title?: string }) {
  return (
    <th title={title} className={cn('erp-grid-header text-left whitespace-nowrap', className)}>
      {children}
    </th>
  )
}

/**
 * A sortable column header.
 *
 * `active`/`dir` come from the `sort=field,dir` query parameter, so a sorted
 * table is a shareable link. Clicking the active column flips the direction;
 * clicking another switches to it ascending.
 */
export function SortTh<F extends string>({
  field, label, active, dir, onSort, className, title,
}: {
  field: F
  label: string
  active: string
  dir: string
  // Generic so a caller with a union of allowed keys keeps that union — a plain
  // `string` here would silently accept a key the backend rejects with a 400.
  onSort: (field: F) => void
  className?: string
  title?: string
}) {
  const on = active === field
  return (
    <Th className={className} title={title}>
      <button
        type="button"
        onClick={() => onSort(field)}
        // Tailwind's preflight resets `text-transform` on <button>, so the
        // uppercase from `.erp-grid-header` does not reach through. Without this
        // the sortable headers render Title Case beside uppercase static ones.
        className={cn(
          'inline-flex items-center gap-1 uppercase tracking-wide hover:text-foreground',
          on && 'text-chocolate',
        )}
        aria-sort={on ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      >
        {label}
        {on && (dir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
      </button>
    </Th>
  )
}

export function Td({
  children, className, title,
}: { children?: ReactNode; className?: string; title?: string }) {
  return <td title={title} className={cn('erp-grid-cell', className)}>{children}</td>
}

export function EmptyRow({ colSpan, message }: { colSpan: number; message: string }) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-4 py-10 text-center text-erp-sm text-muted-foreground">
        {message}
      </td>
    </tr>
  )
}
